import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import App from './App';
import { msalInstance } from './auth/msalInstance';
import './styles/tokens.css';
import './styles/global.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

/**
 * Self-service sign-in reset from the i-broker launcher.
 * See design-system/AUTH-RESET-PATTERN.md.
 *
 * MSAL caches per ORIGIN, so the launcher can't clear this app's storage —
 * it signals us with ?authreset=1 instead. We wipe this origin's MSAL cache
 * and strip the param (one-shot, so it can't loop).
 *
 * ORDER MATTERS: this must complete BEFORE msalInstance.initialize() reads
 * the cache. msalInstance is constructed at import time but doesn't touch
 * localStorage until initialize(), so a synchronous wipe here is safe and
 * lands first. clearCache() is async, but we deliberately don't await it at
 * module scope — the synchronous localStorage sweep below is the one that
 * guarantees the cache is gone before initialize() runs.
 */
function handleAuthReset(): void {
  const sp = new URLSearchParams(window.location.search);
  if (sp.get('authreset') !== '1') return;
  try {
    // Best-effort MSAL API call (async; the sweep below is the guarantee).
    void msalInstance.clearCache?.();
  } catch {
    /* ignore */
  }
  try {
    // Belt-and-braces synchronous wipe of residual MSAL keys.
    for (const k of Object.keys(localStorage)) {
      if (/msal|login\.windows|b2clogin|account\.keys/i.test(k)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
  // Strip the flag so a reload can't loop.
  sp.delete('authreset');
  const q = sp.toString();
  history.replaceState(
    null,
    '',
    window.location.pathname + (q ? '?' + q : '') + window.location.hash
  );
}
handleAuthReset();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Popup-callback short-circuit. See design-system/IFRAMED-AUTH-PATTERN.md
 * Piece 2 — this is the load-bearing change for iframed sign-in.
 *
 * When we're the MSAL popup window (opened from the iframe, returned from
 * Microsoft with an OAuth response in the URL), do NOTHING with MSAL or
 * React — just broadcast the response to the opener's BroadcastChannel and
 * close. MSAL Browser v5 uses BroadcastChannel (not URL polling); the
 * dedicated `broadcastResponseToMainFrame()` helper does exactly this.
 *
 * "We're a popup" needs TWO signals because either can be absent:
 *  - window.opener — present for silent-SSO popups, but SEVERED by
 *    Cross-Origin-Opener-Policy on a full interactive login (password/MFA).
 *  - window.name — MSAL sets it to `msal.<clientId>…`; survives the
 *    cross-origin round trip, so it's the reliable signal when opener is
 *    severed. (Without this, sign-in works for already-signed-in accounts
 *    but hangs for fresh logins — see pattern doc wrong-pattern #7.)
 */
const hasOAuthResponse = (s: string): boolean =>
  /(^|[#&?])(code|access_token|id_token|error)=/.test(s);
const looksLikePopup =
  typeof window !== 'undefined' &&
  ((!!window.opener && window.opener !== window) ||
    (window.name || '').startsWith('msal.'));
const isPopupCallback =
  looksLikePopup &&
  (hasOAuthResponse(window.location.hash) ||
    hasOAuthResponse(window.location.search));

function showFallbackHint(msg: string) {
  const root = document.getElementById('root');
  if (root && !root.firstChild) {
    root.innerHTML =
      '<div style="font-family:Inter,sans-serif;padding:24px;color:#334155;text-align:center">' +
      msg +
      '</div>';
  }
}

if (isPopupCallback) {
  broadcastResponseToMainFrame().catch((err) => {
    console.error('redirect-bridge broadcastResponseToMainFrame failed', err);
    showFallbackHint('Sign-in handler error. You can close this window and try again.');
  });
  // Fallback hint in case window.close() is blocked (very rare).
  setTimeout(() => showFallbackHint('Sign-in complete. You can close this window.'), 3000);
} else {
  msalInstance
    .initialize()
    .then(() => msalInstance.handleRedirectPromise())
    .then((result) => {
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else if (!msalInstance.getActiveAccount()) {
        const cached = msalInstance.getAllAccounts();
        if (cached.length > 0) {
          msalInstance.setActiveAccount(cached[0]);
        }
      }
    })
    .catch((err) => {
      console.error('MSAL init/redirect handling failed', err);
    })
    .finally(() => {
      createRoot(rootEl).render(
        <StrictMode>
          <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </QueryClientProvider>
          </MsalProvider>
        </StrictMode>
      );
    });
}
